import { useCallback, useEffect } from 'react';
import { Button, Form, Input, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CentersDropdown from '../../../components/CentersDropdown';
import CountryDropdown from '../../../components/CountryDropdown';
import StateDropdown from '../../../components/StateDropdown';
import CityDropdown from '../../../components/CityDropdown';
import ZonesDropdown from '../../../components/ZonesDropdown';
import RegionsDropdown from '../../../components/RegionsDropdown';

import {
	Gender,
	PreworkCandidateFilter,
	Country,
} from '../../../shared/PreWorkServiceTypes';
import {
	COUNTRY_CODE_INDIA,
	Gender as GENDER,
	VALID_ABHYASI_ID_REGEX,
	VALID_NAME_REGEX,
	VALID_PINCODE_REGEX,
} from '../../../shared/Constants';
import { useAuth } from '../../../hooks/useAuth';
import { useToastMessage } from '../../../hooks/useToastMessage';
import useUserSession from '../../../hooks/useUserSession';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';

interface FilterProps {
	value: PreworkCandidateFilter;
	onFilterChange: (value: PreworkCandidateFilter) => void;
	onDashboardDetailFilterChange?: (value: PreworkCandidateFilter) => void;
	isMentorsFilter?: boolean;
	formName: string;
	activeTabKey?: string | null;
	selectedBatchId: number | undefined;
}

const { Text } = Typography;

interface RegionOption {
	value: number | undefined;
	label: string | undefined;
}

interface CandidatesFilterFormType {
	srcmId?: string;
	name?: string;
	email?: string;
	mobile?: string;
	country?: string;
	stateId?: Country;
	cityId?: Country;
	centerId?: Country;
	countryId?: Country;
	postalCode?: string;
	regionNames?: RegionOption;
	zoneNames?: Country;
	batch?: string;
	gender?: Gender;
}

export const FilterCandidatesPreWork = ({
	value,
	onFilterChange,
	onDashboardDetailFilterChange,
	isMentorsFilter,
	formName,
	selectedBatchId,
}: FilterProps) => {
	const { t } = useTranslation();
	const { srcmId } = useAuth();
	const { showToastMessage } = useToastMessage();
	const { selectedRole } = useUserSession();
	const [form] = Form.useForm<CandidatesFilterFormType>();
	const values = Form.useWatch<CandidatesFilterFormType>([], form);
	const selectedRegion = Form.useWatch<Country>('regionNames', form);
	const selectedRegionId =
		typeof selectedRegion?.value === 'number'
			? selectedRegion.value
			: undefined;
	const isAdmin = selectedRole === UserRole.SNP_ADMIN;
	const isExamAdmin = selectedRole === UserRole.EXAM_ADMIN;

	const handleFilterButtonClick = useCallback(() => {
		const errors = form.getFieldsError();
		const hasErrors = errors.some((field) => field.errors.length > 0);

		if (hasErrors) {
			showToastMessage('error', t('pleaseEnterValidData'));
			return;
		}

		const filterValues: PreworkCandidateFilter = {
			...values,
			countryIds: values?.countryId?.value
				? ([values?.countryId?.value] as Array<number>)
				: ([COUNTRY_CODE_INDIA] as Array<number>),
			centerIds: [values?.centerId?.value] as Array<number>,
			zoneNames: values?.zoneNames?.label as string,
			cityIds: [values?.cityId?.value] as Array<number>,
			stateIds: [values?.stateId?.value] as Array<number>,
			batch: values.batch as string,
			srcmId: values.srcmId as string,
		};

		if (isMentorsFilter) {
			const mentorsFilter = { ...value, ...filterValues };
			mentorsFilter.showMentors = true;
			onFilterChange(mentorsFilter);
		} else if (onDashboardDetailFilterChange) {
			const candidatesFilter = { ...filterValues };
			candidatesFilter.showAllCandidates = true;
			candidatesFilter.batchId = selectedBatchId;
			onFilterChange(candidatesFilter);

			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreworkDashboardDetail = true;
			dashboardDetailFilter.batchId = selectedBatchId;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		} else {
			const candidatesFilter = { ...filterValues };
			candidatesFilter.showNewCandidatesForGrouping = true;
			candidatesFilter.batchId = selectedBatchId;
			onFilterChange(candidatesFilter);
		}
	}, [
		form,
		onFilterChange,
		onDashboardDetailFilterChange,
		value,
		values,
		isMentorsFilter,
		showToastMessage,
		t,
		selectedBatchId,
	]);

	const StateValue: string | number | undefined = values?.stateId?.value
		? values?.stateId?.value
		: '';

	const CountryValue: string | number | undefined = values?.countryId?.value
		? values?.countryId?.value
		: '';

	const ZoneValue: string | number | undefined = values?.zoneNames?.label
		? values?.zoneNames?.label
		: '';

	const handleResetForm = useCallback(() => {
		form.resetFields();
		const filterValues: PreworkCandidateFilter = {
			showAllCandidates: value?.showAllCandidates,
			showMentors: value?.showMentors,
			showNewCandidatesForGrouping: value?.showNewCandidatesForGrouping,
			showPreworkDashboardDetail: value?.showPreworkDashboardDetail,
			countryIds: [COUNTRY_CODE_INDIA],
			batchId: selectedBatchId,
		};
		onFilterChange(filterValues);
		if (onDashboardDetailFilterChange) {
			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreworkDashboardDetail = true;
			dashboardDetailFilter.batchId = selectedBatchId;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		}
	}, [
		form,
		value,
		onFilterChange,
		onDashboardDetailFilterChange,
		selectedBatchId,
	]);

	useEffect(() => {
		form.resetFields();
		const filterValues: PreworkCandidateFilter = {
			countryIds: [COUNTRY_CODE_INDIA],
			batchId: selectedBatchId,
		};

		if (isMentorsFilter) {
			const mentorsFilter = { ...filterValues };
			mentorsFilter.showMentors = true;
			onFilterChange(mentorsFilter);
		} else if (onDashboardDetailFilterChange) {
			const candidatesFilter = { ...filterValues };
			candidatesFilter.showAllCandidates = true;
			onFilterChange(candidatesFilter);

			const dashboardDetailFilter = { ...filterValues };
			dashboardDetailFilter.showPreworkDashboardDetail = true;
			onDashboardDetailFilterChange(dashboardDetailFilter);
		} else {
			const candidatesFilter = { ...filterValues };
			candidatesFilter.showNewCandidatesForGrouping = true;
			onFilterChange(candidatesFilter);
		}
	}, [
		form,
		isMentorsFilter,
		onDashboardDetailFilterChange,
		onFilterChange,
		selectedBatchId,
	]);

	return (
		<div className="flex flex-col">
			<Form
				name={formName}
				layout="horizontal"
				size="middle"
				labelCol={{ span: 24 }}
				wrapperCol={{ span: 24 }}
				autoComplete="off"
				form={form}
			>
				<div className="flex flex-col md:mb-0">
					<div className="flex flex-col gap-4 md:flex-row">
						{isMentorsFilter && (
							<>
								<div className="w-full md:w-1/6">
									<Text className="pl-1">{t('mentorName')}</Text>
									<Form.Item
										name={'name'}
										rules={[
											{
												pattern: VALID_NAME_REGEX,
												message: (
													<span>
														{t('numbersAndSpecialCharactersNotAllowed')}
													</span>
												),
											},
										]}
										hasFeedback
										className="mb-4"
									>
										<Input />
									</Form.Item>
								</div>
							</>
						)}
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('candidateAbhiyasiId')}</Text>
							<Form.Item
								name={'srcmId'}
								rules={[
									{
										pattern: VALID_ABHYASI_ID_REGEX,
										message: <span>{t('invalidAbhyasiId')}</span>,
									},
								]}
								hasFeedback
								className="mb-4"
							>
								<Input />
							</Form.Item>
						</div>
						{(isAdmin || isExamAdmin) && (
							<div
								className={`w-full ${
									isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'
								}`}
							>
								<Text className="pl-1">{t('region')}</Text>
								<Form.Item name={'regionNames'} style={{ marginBottom: 2 }}>
									<RegionsDropdown
										functionarySrcmId={srcmId}
										multiple={false}
										onChange={(val) => {
											form.setFieldValue('regionNames', val);
											form.setFieldValue('zoneNames', undefined);
										}}
									/>
								</Form.Item>
								<Text className="text-xs text-gray-500 pl-1">
									{t('selectRegionToSelectZones')}
								</Text>
							</div>
						)}
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('zones')}</Text>
							<Form.Item name={'zoneNames'} className="mb-4">
								<ZonesDropdown
									functionarySrcmId={srcmId}
									multiple={false}
									regionId={selectedRegionId}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('center')}</Text>
							<Form.Item name={'centerId'} className="mb-4">
								<CentersDropdown
									zoneValue={ZoneValue}
									functionarySrcmId={srcmId}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('gender')}</Text>
							<Form.Item name={'gender'} className="mb-4">
								<Select
									options={[
										{
											value: GENDER.MALE,
											label: t('male'),
										},
										{
											value: GENDER.FEMALE,
											label: t('female'),
										},
									]}
								/>
							</Form.Item>
						</div>
						<div
							className={`w-full ${isMentorsFilter ? 'md:w-1/6' : 'md:w-1/5'}`}
						>
							<Text className="pl-1">{t('pincode')}</Text>
							<Form.Item
								name="postalCode"
								rules={[
									{
										pattern: VALID_PINCODE_REGEX,
										message: <span>{t('validPincode')}</span>,
									},
								]}
								hasFeedback
								className="mb-4"
							>
								<Input maxLength={7} />
							</Form.Item>
						</div>
					</div>
					<div className="flex flex-col gap-4 md:flex-row">
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('country')}</Text>
							<Form.Item name={'countryId'} className="mb-4">
								<CountryDropdown />
							</Form.Item>
						</div>
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('state')}</Text>
							<Form.Item name={'stateId'} className="mb-4">
								<StateDropdown CountryValue={CountryValue} />
							</Form.Item>
						</div>
						<div className="flex flex-col w-full min-w-[170px]">
							<Text className="pl-1">{t('cityFilterName')}</Text>
							<Form.Item name={'cityId'} className="mb-4">
								<CityDropdown StateValue={StateValue && StateValue} />
							</Form.Item>
						</div>
					</div>
				</div>
			</Form>
			<div className="flex flex-row gap-2 md:mb-2">
				<div className="self-start my-2">
					<Button type="primary" onClick={handleFilterButtonClick}>
						{t('search')}
					</Button>
				</div>
				<div className="self-start my-2">
					<Button type="default" onClick={handleResetForm}>
						{t('clear')}
					</Button>
				</div>
			</div>
		</div>
	);
};
